import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ProviderType,
  UniversalBooking,
  CreateBookingInput,
} from "@/types/booking";

interface UseUniversalBookingsOptions {
  clientView?: boolean;
}

export function useUniversalBookings(
  providerType: ProviderType | undefined,
  providerId: string | undefined,
  options?: UseUniversalBookingsOptions
) {
  const [bookings, setBookings] = useState<UniversalBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("universal_bookings")
        .select(`
          *,
          service:universal_booking_services(id, name, duration_minutes, price)
        `)
        .order("booking_date", { ascending: false })
        .order("start_time", { ascending: false });

      // Filter by provider if provided
      if (providerType && providerId) {
        query = query
          .eq("provider_type", providerType)
          .eq("provider_id", providerId);
      }

      // If client view, filter by current user
      if (options?.clientView) {
        const { data: userData } = await supabase.auth.getUser();
        if (userData.user) {
          query = query.eq("client_user_id", userData.user.id);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching bookings:", error);
        toast.error("Error al cargar reservas");
        return;
      }

      // Map the service join to partial type - we only select specific fields
      const mappedBookings = (data || []).map(booking => ({
        ...booking,
        service: booking.service ? {
          id: booking.service.id,
          name: booking.service.name,
          duration_minutes: booking.service.duration_minutes,
          price: booking.service.price,
        } : undefined,
      }));
      setBookings(mappedBookings as unknown as UniversalBooking[]);
    } catch (err) {
      console.error("Error in fetchBookings:", err);
    } finally {
      setIsLoading(false);
    }
  }, [providerType, providerId, options?.clientView]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const createBooking = async (
    input: CreateBookingInput
  ): Promise<UniversalBooking | null> => {
    try {
      // Get current user if logged in
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("universal_bookings")
        .insert({
          provider_type: input.provider_type,
          provider_id: input.provider_id,
          service_id: input.service_id,
          selected_location_id: input.selected_location_id || null,
          booking_date: input.booking_date,
          start_time: input.start_time,
          end_time: input.end_time,
          client_name: input.client_name,
          client_email: input.client_email,
          client_phone: input.client_phone || null,
          client_notes: input.client_notes || null,
          client_user_id: userData.user?.id || null,
          payment_method: input.payment_method,
          total_amount: input.total_amount,
          status: "pending",
          payment_status: "pending",
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating booking:", error);
        toast.error("Error al crear reserva");
        return null;
      }

      await fetchBookings();
      return data as UniversalBooking;
    } catch (err) {
      console.error("Error in createBooking:", err);
      toast.error("Error inesperado");
      return null;
    }
  };

  const updateBookingStatus = async (
    bookingId: string,
    status: UniversalBooking["status"],
    additionalData?: Partial<UniversalBooking>
  ): Promise<boolean> => {
    try {
      const updateData: Partial<UniversalBooking> = {
        status,
        ...additionalData,
      };

      // Set cancelled_at if cancelling
      if (status === "cancelled") {
        updateData.cancelled_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("universal_bookings")
        .update(updateData)
        .eq("id", bookingId);

      if (error) {
        console.error("Error updating booking status:", error);
        toast.error("Error al actualizar reserva");
        return false;
      }

      toast.success("Reserva actualizada");
      await fetchBookings();
      return true;
    } catch (err) {
      console.error("Error in updateBookingStatus:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const updatePaymentStatus = async (
    bookingId: string,
    paymentStatus: UniversalBooking["payment_status"],
    paymentDetails?: {
      payment_intent_id?: string;
      paypal_order_id?: string;
    }
  ): Promise<boolean> => {
    try {
      const updateData: Partial<UniversalBooking> = {
        payment_status: paymentStatus,
        ...paymentDetails,
      };

      // Auto-confirm if payment is successful
      if (paymentStatus === "paid") {
        updateData.status = "confirmed";
      }

      const { error } = await supabase
        .from("universal_bookings")
        .update(updateData)
        .eq("id", bookingId);

      if (error) {
        console.error("Error updating payment status:", error);
        toast.error("Error al actualizar pago");
        return false;
      }

      await fetchBookings();
      return true;
    } catch (err) {
      console.error("Error in updatePaymentStatus:", err);
      toast.error("Error inesperado");
      return false;
    }
  };

  const getBookedSlotsForDate = async (
    date: string
  ): Promise<{ start_time: string; end_time: string }[]> => {
    if (!providerType || !providerId) return [];

    try {
      const { data, error } = await supabase
        .from("universal_bookings")
        .select("start_time, end_time")
        .eq("provider_type", providerType)
        .eq("provider_id", providerId)
        .eq("booking_date", date)
        .in("status", ["pending", "confirmed"]);

      if (error) {
        console.error("Error fetching booked slots:", error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error("Error in getBookedSlotsForDate:", err);
      return [];
    }
  };

  // Get bookings by status
  const pendingBookings = bookings.filter((b) => b.status === "pending");
  const confirmedBookings = bookings.filter((b) => b.status === "confirmed");
  const upcomingBookings = bookings.filter(
    (b) =>
      (b.status === "pending" || b.status === "confirmed") &&
      new Date(b.booking_date) >= new Date(new Date().toDateString())
  );
  const pastBookings = bookings.filter(
    (b) =>
      b.status === "completed" ||
      new Date(b.booking_date) < new Date(new Date().toDateString())
  );

  return {
    bookings,
    pendingBookings,
    confirmedBookings,
    upcomingBookings,
    pastBookings,
    isLoading,
    createBooking,
    updateBookingStatus,
    updatePaymentStatus,
    getBookedSlotsForDate,
    refetch: fetchBookings,
  };
}
